import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Volume2, ChevronLeft, CheckCircle, XCircle, Sparkles, Loader2, Clock, Flame } from 'lucide-react';
import { apiCall, speak, randomEncouragement } from './api';
import { toast } from 'sonner';

const TOTAL_SECONDS = 300; // 5 minutes

export default function QuickSession({ token, onBack, onUserUpdate }) {
  const [data, setData]   = useState(null);
  const [step, setStep]   = useState(0); // 0 lecture, 1 exo, 2 audio, 3 done
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [seconds, setSeconds] = useState(TOTAL_SECONDS);

  useEffect(() => {
    apiCall('/quick-session', 'GET', null, token).then(setData).catch(e => toast.error(e.message));
  }, [token]);

  useEffect(() => {
    if (step >= 3) return;
    if (seconds <= 0) return;
    const t = setInterval(() => setSeconds(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [step, seconds]);

  const checkExo = () => {
    if (!data?.exercise) return;
    const ok = (answer || '').trim().toLowerCase() === (data.exercise.answer || '').trim().toLowerCase();
    setFeedback(ok ? 'ok' : 'ko');
    setTimeout(() => { setFeedback(null); setStep(2); }, 1400);
  };

  const finishAll = async () => {
    try {
      const r = await apiCall('/progress', 'POST', { lesson_id: data.lesson_id, step: 1, score: 100 }, token);
      onUserUpdate?.(prev => prev ? { ...prev, xp: r.total_xp } : prev);
      toast.success(`${randomEncouragement()} +${r.xp_gained} XP`);
    } catch {}
    setStep(3);
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const pct = ((TOTAL_SECONDS - seconds) / TOTAL_SECONDS) * 100;
  const danger = seconds < 60;

  if (!data) return <div className="max-w-2xl mx-auto px-4 py-12 text-center text-white/60"><Loader2 className="animate-spin mx-auto mb-3" size={28} />Préparation de ta session...</div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto px-4 py-8" data-testid="quick-session">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-4 flex items-center gap-1"><ChevronLeft size={16} />Retour</button>

      {/* Timer header */}
      {step < 3 && (
        <div className="bg-slate-900 border border-white/10 rounded-2xl p-3 mb-3 flex items-center gap-3" data-testid="qs-timer">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${danger ? 'bg-red-500/20 text-red-300 animate-pulse' : 'bg-emerald-500/20 text-emerald-300'}`}>
            <Clock size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between text-xs text-white/60 mb-1">
              <span className={`font-mono font-bold ${danger ? 'text-red-300' : 'text-emerald-200'}`}>{fmt(seconds)}</span>
              <span>Étape {step + 1} / 3</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className={`h-full transition-all duration-1000 ${danger ? 'bg-red-400' : 'bg-emerald-400'}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-emerald-900/40 to-slate-900 border border-emerald-500/30 rounded-3xl p-6">
        <div className="flex items-center gap-2 mb-2"><Sparkles size={16} className="text-yellow-300" /><span className="text-xs text-yellow-300 uppercase tracking-wider">Session rapide · 5 min</span></div>
        <h2 className="text-xl font-bold mb-1">{data.lesson_title}</h2>
        <div className="flex gap-2 my-4">
          {[0,1,2].map(s => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? 'bg-emerald-400' : 'bg-white/10'}`} />)}
        </div>

        {step === 0 && data.lecture && (
          <div className="bg-white/5 rounded-2xl p-5">
            <div className="text-xs text-emerald-300 uppercase tracking-wider mb-2">Étape 1 · Mot du jour</div>
            <div className="text-2xl font-bold text-emerald-200">{data.lecture.tgt}</div>
            <div className="text-white/60 mt-1">{data.lecture.src}</div>
            {data.lecture.note && <div className="text-xs text-white/40 mt-2 italic">{data.lecture.note}</div>}
            <div className="flex items-center gap-2 mt-4">
              <button onClick={() => speak(data.lecture.tgt)} data-testid="qs-speak" className="bg-emerald-500/20 hover:bg-emerald-500/30 p-3 rounded-lg"><Volume2 size={18} className="text-emerald-300" /></button>
              <button onClick={() => setStep(1)} data-testid="qs-next-1" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-3 rounded-xl">Continuer</button>
            </div>
          </div>
        )}

        {step === 1 && data.exercise && (
          <div className="bg-white/5 rounded-2xl p-5">
            <div className="text-xs text-emerald-300 uppercase tracking-wider mb-2">Étape 2 · Exercice</div>
            <div className="text-lg font-bold mb-3">{data.exercise.question}</div>
            <input value={answer} onChange={e => setAnswer(e.target.value)} onKeyDown={e => e.key === 'Enter' && checkExo()}
              placeholder="Votre réponse..." className="w-full bg-slate-900 border border-white/10 rounded-lg p-3 text-white" data-testid="qs-input" />
            {data.exercise.hint && <div className="text-xs text-white/40 mt-2">💡 {data.exercise.hint}</div>}
            {feedback && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${feedback === 'ok' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                {feedback === 'ok' ? <><CheckCircle size={14} className="inline mr-1" />Correct !</> : <><XCircle size={14} className="inline mr-1" />Réponse : <strong>{data.exercise.answer}</strong></>}
              </div>
            )}
            {!feedback && <button onClick={checkExo} disabled={!answer} className="mt-4 w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold py-3 rounded-xl" data-testid="qs-check">Valider</button>}
          </div>
        )}

        {step === 2 && data.audio && (
          <div className="bg-white/5 rounded-2xl p-5 text-center">
            <div className="text-xs text-emerald-300 uppercase tracking-wider mb-2">Étape 3 · Écoute & répète</div>
            <div className="text-xl font-bold">{data.audio.text}</div>
            <div className="text-sm text-white/60 mt-1 mb-4">{data.audio.translation}</div>
            <button onClick={() => speak(data.audio.text)} data-testid="qs-audio-play" className="bg-emerald-500 text-slate-950 font-bold px-6 py-2.5 rounded-lg flex items-center gap-2 mx-auto"><Volume2 size={16} />Écouter</button>
            <button onClick={finishAll} data-testid="qs-finish" className="mt-4 w-full bg-yellow-500 hover:bg-yellow-600 text-slate-950 font-bold py-3 rounded-xl">Terminer la session</button>
          </div>
        )}

        {step === 3 && (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 text-center">
            <div className="text-6xl mb-3">🔥</div>
            <div className="font-bold text-2xl">Session terminée !</div>
            <div className="text-sm text-white/60 mt-1">{randomEncouragement()}</div>
            <div className="bg-yellow-400 text-slate-950 rounded-xl py-3 px-4 my-4 inline-flex items-center gap-2 font-bold text-lg">
              <Flame size={20} />+10 XP gagné
            </div>
            <div className="text-xs text-white/50">Temps utilisé : {fmt(TOTAL_SECONDS - seconds)}</div>
            <button onClick={onBack} className="mt-5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold px-6 py-2.5 rounded-xl" data-testid="qs-back">Retour au tableau de bord</button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
